'use client';
import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import StateManagedSelect$1 from '../../../node_modules/react-select/dist/react-select.esm.js';
import { useTheme } from 'styled-components';
import customStyles, { Container, DropdownLabel } from './Dropdown.styles.js';
import { DropdownIndicator } from './DropdownIndicator.js';
import { optionPropType } from './dropdownPropTypes.js';
import { DROPDOWN_SIZE, DROPDOWN_VARIANT, MENU_POSITION, MENU_ALIGNMENT, MENU_PLACEMENT, LABEL_POSITION } from './Dropdown.constants.js';
import { ClearIndicator, MultiValue, MultiValueLabel, MultiValueRemove, MultiOption } from './Dropdown.multiSelect.js';
import useRect from '../../../hooks/useRect.js';
import ScreenReaderOnly from '../../ScreenReaderOnly/index.js';
import { LoadingIndicator } from './LoadingIndicator.js';
import { c as components } from '../../../node_modules/react-select/dist/index-a301f526.esm.js';

var _excluded = ["getValue", "children"];
var ValueContainer = function ValueContainer(_ref) {
  var _ref$getValue = _ref.getValue,
    getValue = _ref$getValue === void 0 ? function () {} : _ref$getValue,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var selectedOptions = getValue();
  var selectedTotal = selectedOptions.length;
  var _useRect = useRect(undefined, [selectedTotal]),
    _useRect2 = _slicedToArray(_useRect, 2),
    ref = _useRect2[0],
    rect = _useRect2[1];
  var setValueContainerWidth = rest.selectProps.setValueContainerWidth;
  useLayoutEffect(function () {
    setValueContainerWidth(rect.width);
  }, [rect.width]);
  return (React__default.createElement(components.ValueContainer, _extends({}, rest, {
      innerProps: {
        ref: ref
      }
    }), children)
  );
};
var Dropdown = function Dropdown(_ref2) {
  var id = _ref2.id,
    _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? DROPDOWN_SIZE.REGULAR : _ref2$size,
    label = _ref2.label,
    _ref2$value = _ref2.value,
    value = _ref2$value === void 0 ? null : _ref2$value,
    _ref2$options = _ref2.options,
    options = _ref2$options === void 0 ? [] : _ref2$options,
    _ref2$variant = _ref2.variant,
    variant = _ref2$variant === void 0 ? DROPDOWN_VARIANT.PRIMARY : _ref2$variant,
    onChange = _ref2.onChange,
    _ref2$onInputChange = _ref2.onInputChange,
    onInputChange = _ref2$onInputChange === void 0 ? function () {} : _ref2$onInputChange,
    _ref2$isLoading = _ref2.isLoading,
    isLoading = _ref2$isLoading === void 0 ? false : _ref2$isLoading,
    _ref2$isMulti = _ref2.isMulti,
    isMulti = _ref2$isMulti === void 0 ? false : _ref2$isMulti,
    _ref2$components = _ref2.components,
    components = _ref2$components === void 0 ? {} : _ref2$components,
    _ref2$isDisabled = _ref2.isDisabled,
    isDisabled = _ref2$isDisabled === void 0 ? false : _ref2$isDisabled,
    _ref2$placeholder = _ref2.placeholder,
    placeholder = _ref2$placeholder === void 0 ? 'Select' : _ref2$placeholder,
    _ref2$isSearchable = _ref2.isSearchable,
    isSearchable = _ref2$isSearchable === void 0 ? true : _ref2$isSearchable,
    _ref2$menuPosition = _ref2.menuPosition,
    menuPosition = _ref2$menuPosition === void 0 ? MENU_POSITION.ABSOLUTE : _ref2$menuPosition,
    _ref2$menuAlignment = _ref2.menuAlignment,
    menuAlignment = _ref2$menuAlignment === void 0 ? MENU_ALIGNMENT.LEFT : _ref2$menuAlignment,
    _ref2$menuPlacement = _ref2.menuPlacement,
    menuPlacement = _ref2$menuPlacement === void 0 ? MENU_PLACEMENT.BOTTOM : _ref2$menuPlacement,
    _ref2$labelPosition = _ref2.labelPosition,
    labelPosition = _ref2$labelPosition === void 0 ? LABEL_POSITION.TOP : _ref2$labelPosition,
    _ref2$filterOption = _ref2.filterOption,
    filterOption = _ref2$filterOption === void 0 ? undefined : _ref2$filterOption,
    _ref2$noOptionsText = _ref2.noOptionsText,
    noOptionsText = _ref2$noOptionsText === void 0 ? 'No options' : _ref2$noOptionsText,
    _ref2$selectedChipTex = _ref2.selectedChipText,
    selectedChipText = _ref2$selectedChipTex === void 0 ? 'selected' : _ref2$selectedChipTex,
    _ref2$formatOptionLab = _ref2.formatOptionLabel,
    formatOptionLabel = _ref2$formatOptionLab === void 0 ? undefined : _ref2$formatOptionLab,
    _ref2$testId = _ref2.testId,
    testId = _ref2$testId === void 0 ? undefined : _ref2$testId;
  var theme = useTheme();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    valueContainerWidth = _useState2[0],
    setValueContainerWidth = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    multiSelectContainerWidth = _useState4[0],
    setMultiSelectContainerWidth = _useState4[1];
  var extraWidth = isSearchable ? 12 : 4;
  var displayMultiValueItems = valueContainerWidth < multiSelectContainerWidth + extraWidth;
  var isGrouped = options.some(function (option) {
    return option.options;
  });
  var enableMultipleOptions = isMulti ? {
    Option: MultiOption
  } : {};
  var styles = customStyles({
    isGrouped: isGrouped,
    menuAlignment: menuAlignment,
    menuPlacement: menuPlacement,
    menuPosition: menuPosition,
    size: size,
    theme: theme,
    variant: variant
  });
  var isScreenReaderOnly = labelPosition === LABEL_POSITION.NONE || size === DROPDOWN_SIZE.TINY;
  return React__default.createElement(Container, {
    $labelPosition: labelPosition,
    "data-testid": testId
  }, isScreenReaderOnly && React__default.createElement(ScreenReaderOnly, {
    as: "label",
    htmlFor: id
  }, label), React__default.createElement(DropdownLabel, {
    id: "label-".concat(id),
    htmlFor: id,
    "aria-label": label,
    $labelPosition: labelPosition,
    $size: size
  }, label), React__default.createElement(StateManagedSelect$1, {
    id: "select-".concat(id),
    isSearchable: isSearchable,
    formatOptionLabel: formatOptionLabel,
    styles: styles,
    components: _objectSpread2(_objectSpread2({
      ClearIndicator: ClearIndicator,
      DropdownIndicator: isLoading ? null : DropdownIndicator,
      IndicatorSeparator: null,
      LoadingIndicator: LoadingIndicator,
      MultiValue: MultiValue,
      MultiValueLabel: MultiValueLabel,
      MultiValueRemove: MultiValueRemove,
      ValueContainer: ValueContainer
    }, enableMultipleOptions), components),
    setValueContainerWidth: setValueContainerWidth,
    setMultiSelectContainerWidth: setMultiSelectContainerWidth,
    displayMultiValueItems: displayMultiValueItems,
    inputId: id,
    value: value,
    options: options,
    filterOption: filterOption,
    onChange: onChange,
    onInputChange: onInputChange,
    isLoading: isLoading,
    isMulti: isMulti,
    isDisabled: isDisabled || isLoading,
    placeholder: placeholder,
    menuPlacement: menuPlacement,
    hideSelectedOptions: false,
    closeMenuOnSelect: !isMulti,
    noOptionsMessage: function noOptionsMessage() {
      return noOptionsText;
    },
    selectedChipText: selectedChipText
  }));
};
Dropdown.propTypes = {
  components: PropTypes.objectOf(PropTypes.func),
  filterOption: PropTypes.func,
  formatOptionLabel: PropTypes.func,
  id: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  label: PropTypes.node.isRequired,
  labelPosition: PropTypes.oneOf(Object.values(LABEL_POSITION)),
  menuAlignment: PropTypes.oneOf(Object.values(MENU_ALIGNMENT)),
  menuPlacement: PropTypes.oneOf(Object.values(MENU_PLACEMENT)),
  menuPosition: PropTypes.oneOf(Object.values(MENU_POSITION)),
  noOptionsText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  options: PropTypes.arrayOf(optionPropType),
  placeholder: PropTypes.node,
  selectedChipText: PropTypes.string,
  size: PropTypes.oneOf(Object.values(DROPDOWN_SIZE)),
  testId: PropTypes.string,
  value: PropTypes.oneOfType([optionPropType, PropTypes.arrayOf(optionPropType)]),
  variant: PropTypes.oneOf(Object.values(DROPDOWN_VARIANT))
};
var DropdownComponents = components;

export { Dropdown, DropdownComponents, ValueContainer };
//# sourceMappingURL=Dropdown.js.map
